<template>
	<ErrorModal />
	<Toast />
	<ConfirmDialog></ConfirmDialog>

	<div class="card-container pageheader">

		<div class="top">
            <InputText v-model="emailSearch" type="email" placeholder="Valid email addresses only" style="width:16.5rem" />
            <Button class="p-button p-component btn-secondary p-button-rounded sml" type="button" @click="searchUser" :loading="loading">Search</Button>
		</div>
	</div>

	<div class="container" :hidden="!searched">
        <div class="info-card">
            <div class="info-grid" v-if="userData.email !== ''">
                <div class="info-column">
                    <p class="info-item"><strong>Name:</strong><span>{{ userData.name }}</span></p>
                    <p class="info-item"><strong>Email:</strong><span>{{ userData.email }}</span></p>
                    <p class="info-item"><strong>Role:</strong><span>{{ userData.user_metadata.role }}</span></p>
                    <p class="info-item"><strong>Blocked:</strong><span>{{ userData.blocked ? 'Yes' : 'No' }}</span></p>
                </div>
                <div class="info-column">
                    <p class="info-item">
                        <strong>Store Name:</strong>
                        <span class="tooltip" @click="copyToClipBoard(userData.user_metadata.storeid)">
                            {{ userData.storename }}
                            <span class="tooltiptext tooltip-top">
                                {{ userData.user_metadata.storeid }}
                                <i class="pi pi-copy" />
                            </span>
                        </span>
                    </p>
                    <!-- <p class="info-item"><strong>Store ID:</strong><span>{{ userData.user_metadata.storeid }}</span></p> -->
                    <p class="info-item"><strong>Date Created:</strong><span>{{ formatRegularDate(userData.created_at) }}</span></p>
                    <p class="info-item"><strong>Last Login:</strong><span>{{ formatRelativeTime(userData.last_login) }}</span></p>
                </div>
            </div>
            <div class="info-grid" v-else>
                User not found.
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { ref, computed } from 'vue';
import { useAuth0 } from "@auth0/auth0-vue";
import { useToast } from 'primevue/usetoast';
import ErrorModal from './ErrorModal.vue';
import moment from 'moment';

const toast = useToast();
const auth0 = useAuth0();
const emailSearch = ref('');
const userData = ref({
    blocked: false,
    email: '',
    name: '',
    user_metadata: {
        role: '',
        storeid: ''
    },
    last_login: '',
    created_at: '',
    storename: ''
});
const loading = ref(false);
const searched = ref(false);

const searchUser = async () => {
    if (emailError.value) {
        toast.add({ severity: 'info', summary: 'Email', detail: 'Please enter a valid email address.', life: 3000 });
        return;
    }

    loading.value = true;
    const accessToken = await auth0.getAccessTokenSilently();
    const response = await fetch(process.env.VUE_APP_NODEJS_BASE_URL + 'sa/email', {
        method: 'POST',
        body: JSON.stringify({ email: emailSearch.value }),
        headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
        },
    });
    const data = await response.json();
    const user = data.message;

    userData.value = {
        blocked: user.blocked ?? false,
        email: user.email ?? '',
        name: user.name ?? '',
        user_metadata: {
            role: user.user_metadata?.role ?? '',
            storeid: user.user_metadata?.storeid ?? ''
        },
        last_login: user.last_login ?? '',
        created_at: user.created_at ?? '',
        storename: user.storename ?? ''
    };
    loading.value = false;
    searched.value = true;
}

const emailError = computed(() => {
  if (!emailSearch.value) return false;
  return !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailSearch.value);
});

const formatRelativeTime = (dateValue) => {
  return moment(dateValue).fromNow();
}

const formatRegularDate = (dateValue) => {
    return moment(dateValue).format('LL'); 
}

const copyToClipBoard = (storeid) => {
    navigator.clipboard.writeText(storeid);
}

</script>

<style>
    .card-container {
        padding: 0 1.5rem;
    }

    .info-card {
        background: #ffffff;
        padding: 20px 24px;
        border: solid 1px #dee2e6;
        margin-bottom: 2rem;
    }

    .info-grid {
      display: flex;
      gap: 3rem;
      align-items: flex-start;
    }

    .info-column {
      flex: 1 1 0;
      min-width: 0;
    }

    .info-item {
      display: flex;
      flex-direction: row;
      gap: 0.25rem;
    }

    .info-item strong {
        width: 7.75rem;
    }

    .info-item span {
      min-width: 0;
      overflow-wrap: anywhere;
      word-break: break-word;
    }

    /* Responsive layout for small screens */
    @media (max-width: 870px) {

        .info-grid {
            flex-direction: column;
            gap: 0;
        }
        
        .info-item {
            flex-direction: column;
            justify-content: space-evenly;
            gap: 0;
        }

    }

    /* Tooltip container */
    .tooltip {
        position: relative;
        display: inline-block;
        border-bottom: 1px dotted black; /* Add dots under the hoverable text */
        cursor: pointer;
    }

    .tooltip-top {
        bottom: 125%;
        left: 50%;
        margin-left: -60px;
    }

    /* Tooltip text */
    .tooltiptext {
        font-size: x-small;
        visibility: hidden;
        background-color: black;
        color: #ffffff;
        text-align: center;
        padding: 6px 10px;
        border-radius: 6px;
        position: absolute;
        z-index: 1;

        white-space: nowrap;      /* 🔑 prevents wrapping */
        width: max-content;       /* 🔑 expands to text */
        max-width: 300px;         /* optional safety */
    }

    /* Show the tooltip text on hover */
    .tooltip:hover .tooltiptext {
        visibility: visible;
    }
</style>